import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Card, CardContent } from 'components/ui/card';
import { Checkbox } from 'components/ui/checkbox';
import { Textarea } from 'components/ui/textarea';
import { Briefcase, ArrowRight, ArrowLeft, MapPin, DollarSign, Globe } from 'lucide-react';
import { toast } from 'sonner';

const SERVICES = [
  "Companionship", "Personal Care", "Medication Reminders",
  "Meal Preparation", "Light Housekeeping", "Transportation",
  "Dementia Care", "Overnight Care", "Respite Care"
];

const LANGUAGES = [
  "English", "Spanish", "Chinese", "Tagalog", "Vietnamese",
  "Korean", "French", "Hindi", "Arabic", "Portuguese",
  "Russian", "Japanese", "German", "Italian", "Polish"
];

export default function CaregiverProfileSetup() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    services: [],
    experience_years: '',
    hourly_rate: '',
    city: '',
    state: '',
    zip_code: '',
    bio: '',
    languages: []
  });

  useEffect(() => {
    api.get('/caregivers/profile').then(res => {
      const p = res.data?.profile;
      if (p) {
        setFormData({
          services: p.services || [],
          experience_years: p.experience_years != null ? String(p.experience_years) : '',
          hourly_rate: p.hourly_rate != null ? String(p.hourly_rate) : '',
          city: p.city || '',
          state: p.state || '',
          zip_code: p.zip_code || '',
          bio: p.bio || '',
          languages: p.languages || []
        });
      }
    }).catch(() => {});
  }, []);

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleLanguageToggle = (lang) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter(l => l !== lang)
        : [...prev.languages, lang]
    }));
  };

  const handleSubmit = async () => {
    if (formData.services.length === 0 || !formData.experience_years || !formData.hourly_rate) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await api.put('/caregivers/profile', {
        services: formData.services,
        experience_years: parseInt(formData.experience_years),
        hourly_rate: parseFloat(formData.hourly_rate),
        city: formData.city || null,
        state: formData.state || null,
        zip_code: formData.zip_code || null,
        bio: formData.bio || null,
        languages: formData.languages
      });
      toast.success('Profile setup complete!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-teal-500 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900 font-['Manrope']">ElderLink</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Caregiver Profile Setup</h1>
          <p className="text-slate-500 mt-2">Set up your profile so families can find you</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`w-24 h-1 rounded-full ${step >= s ? 'bg-teal-500' : 'bg-slate-200'}`} />
          ))}
        </div>

        <Card className="border-0 shadow-xl">
          <CardContent className="p-4 sm:p-8">
            {/* Step 1: Services & Experience */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
                    <Briefcase className="w-8 h-8 text-teal-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900 font-['Manrope']">Services & Experience</h2>
                </div>

                <div className="space-y-4">
                  <div className="space-y-3">
                    <Label>Services You Provide *</Label>
                    <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                      {SERVICES.map((service) => (
                        <label
                          key={service}
                          className={`flex items-center gap-1.5 sm:gap-2 px-2 py-1.5 sm:p-2.5 rounded-md border cursor-pointer transition-colors min-w-0 ${
                            formData.services.includes(service)
                              ? 'border-teal-500 bg-teal-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <Checkbox
                            checked={formData.services.includes(service)}
                            onCheckedChange={() => handleServiceToggle(service)}
                            className="shrink-0"
                          />
                          <span className="text-xs sm:text-sm leading-tight">{service}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Years of Experience *</Label>
                    <Input
                      type="number"
                      placeholder="5"
                      className="h-12"
                      value={formData.experience_years}
                      onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })}
                    />
                  </div>
                </div>

                <Button
                  onClick={() => setStep(2)}
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-lg"
                  disabled={formData.services.length === 0 || !formData.experience_years}
                >
                  Continue
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </div>
            )}

            {/* Step 2: Rate & Location */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
                    <MapPin className="w-8 h-8 text-teal-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900 font-['Manrope']">Rate & Location</h2>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="hourly_rate">Hourly Rate ($/hr) *</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <Input
                        id="hourly_rate"
                        type="number"
                        placeholder="e.g., 25"
                        value={formData.hourly_rate}
                        onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                        className="pl-10 h-12"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      placeholder="e.g., New York"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="h-12"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        placeholder="e.g., NY"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                        className="h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zip">ZIP Code</Label>
                      <Input
                        id="zip"
                        placeholder="e.g., 10001"
                        value={formData.zip_code}
                        onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                        className="h-12"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="flex-1 h-12 rounded-lg"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Back
                  </Button>
                  <Button
                    onClick={() => setStep(3)}
                    className="flex-1 h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-lg"
                    disabled={!formData.hourly_rate}
                  >
                    Continue
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Bio & Languages */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
                    <Globe className="w-8 h-8 text-teal-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900 font-['Manrope']">About You</h2>
                  <p className="text-slate-500 text-sm">Help families get to know you</p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="bio">Bio</Label>
                    <Textarea
                      id="bio"
                      placeholder="Tell families about yourself, your approach to caregiving, and what makes you a great fit..."
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      className="min-h-[120px]"
                    />
                  </div>

                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-slate-500" />
                      Languages Spoken
                    </Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-2">
                      {LANGUAGES.map((lang) => (
                        <label
                          key={lang}
                          className={`flex items-center gap-1.5 px-2 py-1.5 sm:p-2.5 rounded-md border cursor-pointer transition-colors ${
                            formData.languages.includes(lang)
                              ? 'border-teal-500 bg-teal-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <Checkbox
                            checked={formData.languages.includes(lang)}
                            onCheckedChange={() => handleLanguageToggle(lang)}
                            className="shrink-0"
                          />
                          <span className="text-xs sm:text-sm">{lang}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep(2)}
                    className="flex-1 h-12 rounded-lg"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    className="flex-1 h-12 bg-teal-500 hover:bg-teal-600 text-white rounded-lg"
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : 'Complete Setup'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          You can always update this information later in Settings
        </p>
      </div>
    </div>
  );
}
